import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  Input,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { AlertsComponent } from '../../../pillar/alerts/components/alerts.component';
import { ModuleConfiguration } from '../../datawarehouse.module.config';
import * as fromStore from '../../store/reducers';
import * as dashboardActions from '../../../pillar/dashboard/store/actions';
import { StoreDataSource } from '../../../core/services/StoreDataSource';
import { MatPaginator, MatSort, MatDialog } from '@angular/material';
import { SearchComponent } from '../../../pillar/search/components/search.component';
import { Store } from '@ngrx/store';
import { ModelBase } from '../../../pillar/common/models/ModelBase';
import { Router } from '@angular/router';
import { DataSetRootEntityModel } from '../../models/DataSetRootEntityModel';
import { LoadEntityListPayload } from '../../models/LoadEntityListPayload';
import { ColumnMetadataModel } from '../../models/ColumnMetadataModel';
import {
  GetEntityListMetadataAction,
  ReviseRootEntityAction,
  REVISE_ROOT_ENTITY_RESPONSE,
} from '../../store/actions';
import { LookupModel } from '../../../common/models/LookupModel';
import { Actions } from '@ngrx/effects';
import * as actions from '../../store/actions';
import * as moment from 'moment';

@Component({
  selector: 'brd-data-warehouse-entity-list',
  templateUrl: './data-warehouse-entity-list.component.html',
  styleUrls: ['./data-warehouse-entity-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataWarehouseEntityListComponent implements OnInit, OnDestroy {
  displayedColumns: string[];
  columns: ColumnMetadataModel[];
  dataSource: StoreDataSource<any>;
  loading$: Observable<boolean>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  _rootEntityModel: DataSetRootEntityModel;
  _rootEntityRefreshed$: Subject<boolean> = new Subject<boolean>();
  _submittingEntity: LookupModel;
  _loading = false;
  _lastPayload: LoadEntityListPayload;
  showingDeleted: boolean;

  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(SearchComponent)
  search: SearchComponent;
  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  @Input()
  set rootEntityModel(rootEntityModel: DataSetRootEntityModel) {
    this._rootEntityRefreshed$.next(true);
    this._rootEntityRefreshed$ = new Subject<boolean>();
    this._rootEntityModel = rootEntityModel;
    this.sort.active = undefined;
    this.sort.direction = 'asc';
    this.modelRefreshed();
  }

  @Input()
  set submittingEntity(submittingEntity: LookupModel) {
    this._submittingEntity = submittingEntity;
    this.refreshDataSource();
  }

  constructor(
    private store: Store<any>,
    private router: Router,
    private dialog: MatDialog,
    private actions$: Actions,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next(true);
    this._rootEntityRefreshed$.next(true);
  }

  ngOnInit() {
    this.actions$
      .ofType(REVISE_ROOT_ENTITY_RESPONSE)
      .takeUntil(this.destroyed$)
      .subscribe((a: actions.ReviseRootEntityResponseAction) => {
        this._loading = false;
        if(a.payload.model.UI_action == "delete"){
          a.payload.results.ruleViolations.forEach(e => this.alerts.addError(e.message));
        }

        this.store.dispatch(
          new dashboardActions.QueryListAction(
            ModuleConfiguration.moduleName,
            ModelBase,
            this._lastPayload,
          ),
        );

        if(!a.payload.results.hasRuleViolation && a.payload.results.savedEntityKey){
          this.store.dispatch(
            new actions.SyncWithNextgateAction({
              key: a.payload.results.savedEntityKey,
              isNew: false,
              currentAction: a.payload.model.UI_action
            }),
          );
        }
      });
  }

  private modelRefreshed() {
    if (this._rootEntityModel) {
      this.loading$ = this.store.select(
        fromStore.getEditorStateLoading(
          this._rootEntityModel.dataEntityKey,
          'root',
        ),
      );
      this.store
        .select(
          fromStore.getEntityListMetadata(
            this._rootEntityModel.dataEntityKey,
            'root',
          ),
        )
        .takeUntil(this._rootEntityRefreshed$)
        .subscribe(m => {
          if (m) {
            this.columns = m.columns;
            this.displayedColumns = [];
            this.columns.forEach(c => {
              this.displayedColumns.push(c.name);
            });
            this.displayedColumns.push('actions');
            this.refreshDataSource();
          } else {
            this.refresh();
          }
        });
    }
  }

  private refresh() {
    this.columns = undefined;
    this.displayedColumns = [];
    this.dataSource = undefined;
    this.sort.active = undefined;
    this.sort.direction = 'asc';
    this.store.dispatch(
      new GetEntityListMetadataAction({
        entityKey: this._rootEntityModel.dataEntityKey,
        scope: 'root',
      }),
    );
  }

  public refreshDataSource() {
    this.search.writeValue('');
    this.dataSource = new StoreDataSource<any>(
      this._rootEntityRefreshed$,
      this.paginator,
      this.store,
      this.store.select(
        fromStore.getEntitiesList(this._rootEntityModel.dataEntityKey, 'root'),
      ),
      this.store.select(
        fromStore.getEntitiesListTotalCount(
          this._rootEntityModel.dataEntityKey,
          'root',
        ),
      ),
      this.store.select(
        fromStore.getEditorStateLoading(
          this._rootEntityModel.dataEntityKey,
          'root',
        ),
      ),
      (p, s, search) => {
        const payload = new LoadEntityListPayload();
        payload.searchTerm = search.searchControl.value;
        p.pageIndex = p.pageIndex >= 0 ? p.pageIndex : 0;
        payload.page = this._lastPayload != null && this._lastPayload.searchTerm != payload.searchTerm ? 0 : p.pageIndex;
        payload.pageSize = p.pageSize;
        payload.sort = s.active;
        payload.sortDirection = s.direction;
        payload.entityKey = this._rootEntityModel.dataEntityKey;
        payload.scope = 'root';
        payload.showDeleted = this.showingDeleted;
        this._lastPayload = payload;
        return new dashboardActions.QueryListAction(
          ModuleConfiguration.moduleName,
          ModelBase,
          payload,
        );
      },
      this.sort,
      this.search,
    );
  }

  selectItem(item) {
    if (!this.showingDeleted) {
      this.router.navigateByUrl(
        `datawarehouse-workspace/${this._submittingEntity.key}/${this._rootEntityModel.dataEntityKey}/${item.key}`,
      );
    }
  }

  showAdd(): void {
    this.router.navigateByUrl(
      `datawarehouse-workspace/${this._submittingEntity.key}/${this._rootEntityModel.dataEntityKey}/new`,
    );
  }

  showDeleted(): void {
    this.showingDeleted = true;
    this.refreshDataSource();
  }

  dontShowDeleted(): void {
    this.showingDeleted = false;
    this.refreshDataSource();
  }

  deleteSelectItem(item): void {
    this.store.dispatch(
      new ReviseRootEntityAction({
        entityKey: this._rootEntityModel.dataEntityKey,
        key: item.key,
        model: {
          key: item.key,
          UI_action: 'delete',
        },
        scope: 'root',
      }),
    );
    this._loading = true;
  }

  undoDeleteSelectItem(item): void {
    this.store.dispatch(
      new ReviseRootEntityAction({
        entityKey: this._rootEntityModel.dataEntityKey,
        key: item.key,
        model: {
          key: item.key,
          UI_action: 'undo-delete',
        },
        scope: 'root',
      }),
    );
    this._loading = true;
  }

  getDisplayValue(item: any) {
    if (!item) {
      return item;
    }
    if(item instanceof moment){
      return moment(item).format('M/D/YYYY');
    }
    return item.displayName || item.description || item;
  }
}



// WEBPACK FOOTER //
// ./src/app/datawarehouse/components/data-warehouse-entity-list/data-warehouse-entity-list.component.ts